import {
  AppWindow,
  Bell,
  Cable,
  Calendar,
  CreditCard,
  Eye,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  Mail,
  MessageCircle,
  Plug,
  Settings,
  Share2,
  Upload,
  Users,
  Video,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type {
  ProcessingIncidentOperation,
  ProcessingIncidentSurface,
} from "@/services/adminApi";

// Single source of truth for the admin taxonomy display, kept in sync with the
// server's @shared/observability incident taxonomy (ADR-0065). Surfaces render
// as monochrome (zinc) chips with a Lucide glyph — color is reserved for status
// and priority so the product-area facet never competes with severity.

export const INCIDENT_SURFACES: ProcessingIncidentSurface[] = [
  "chat",
  "settings",
  "workspace",
  "team_members",
  "billing",
  "integrations",
  "connectors",
  "file_upload",
  "document_processing",
  "preview",
  "libra_apps",
  "calendar_sync",
  "email_assistant",
  "meetings",
  "workflows",
  "dashboard",
  "share_export",
  "notifications",
];

export const INCIDENT_OPERATIONS: ProcessingIncidentOperation[] = [
  "chat.request_failed",
  "settings.save_failed",
  "workspace.switch_failed",
  "team_members.invite_failed",
  "team_members.role_update_failed",
  "billing.portal_failed",
  "billing.subscription_update_failed",
  "integrations.connect_failed",
  "integrations.oauth_callback_failed",
  "connectors.sync_failed",
  "file_upload.upload_failed",
  "document_processing.parse_failed",
  "document_processing.index_failed",
  "preview.render_failed",
  "preview.load_failed",
  "libra_apps.render_failed",
  "libra_apps.action_failed",
  "calendar_sync.connect_failed",
  "calendar_sync.sync_failed",
  "email_assistant.generate_failed",
  "email_assistant.send_failed",
  "meetings.transcript_failed",
  "meetings.summary_failed",
  "workflows.execution_failed",
  "dashboard.load_failed",
  "share_export.export_failed",
  "notifications.delivery_failed",
];

export const SURFACE_META: Record<
  ProcessingIncidentSurface,
  { label: string; icon: LucideIcon }
> = {
  chat: { label: "Chat", icon: MessageCircle },
  settings: { label: "Settings", icon: Settings },
  workspace: { label: "Workspace", icon: LayoutGrid },
  team_members: { label: "Team members", icon: Users },
  billing: { label: "Billing", icon: CreditCard },
  integrations: { label: "Integrations", icon: Plug },
  connectors: { label: "Connectors", icon: Cable },
  file_upload: { label: "File upload", icon: Upload },
  document_processing: { label: "Document processing", icon: FileText },
  preview: { label: "Preview", icon: Eye },
  libra_apps: { label: "Libra Apps", icon: AppWindow },
  calendar_sync: { label: "Calendar sync", icon: Calendar },
  email_assistant: { label: "Email Assistant", icon: Mail },
  meetings: { label: "Meetings", icon: Video },
  workflows: { label: "Workflows", icon: Workflow },
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  share_export: { label: "Share / export", icon: Share2 },
  notifications: { label: "Notifications", icon: Bell },
};

/** Operations grouped under their surface — drives the scoped operation filter. */
export const SURFACE_OPERATIONS: Record<
  ProcessingIncidentSurface,
  ProcessingIncidentOperation[]
> = INCIDENT_SURFACES.reduce(
  (acc, surface) => {
    acc[surface] = INCIDENT_OPERATIONS.filter((op) => op.startsWith(`${surface}.`));
    return acc;
  },
  {} as Record<ProcessingIncidentSurface, ProcessingIncidentOperation[]>,
);

export function surfaceLabel(surface: ProcessingIncidentSurface): string {
  return SURFACE_META[surface]?.label ?? surface;
}

/** Humanize the operation verb, e.g. "settings.save_failed" -> "Save failed". */
export function operationLabel(
  operation: ProcessingIncidentOperation | null | undefined,
): string {
  // Null-safe like surfaceLabel/SurfaceChip: a row whose incidentOperation the
  // API omits (e.g. an older server during a deploy) must not throw and
  // white-screen the whole list.
  if (!operation) return "Unknown";
  const verb = operation.includes(".") ? operation.slice(operation.indexOf(".") + 1) : operation;
  const spaced = verb.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Monochrome surface chip: glyph + label, zinc ring. */
export function SurfaceChip({ surface }: { surface: ProcessingIncidentSurface }) {
  const meta = SURFACE_META[surface];
  const Icon = meta?.icon ?? MessageCircle;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-50 px-2.5 py-1 text-[11px] font-medium text-zinc-600 ring-1 ring-inset ring-zinc-200/70">
      <Icon className="h-3 w-3 text-zinc-400" />
      {surfaceLabel(surface)}
    </span>
  );
}

/** Quiet badge marking a synthetic test incident (never a real customer disruption). */
export function TestBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-zinc-500 ring-1 ring-inset ring-zinc-200/70">
      Test
    </span>
  );
}
